import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Accordion, AccordionDetails, AccordionSummary, Button, CircularProgress, Divider, IconButton, Menu, MenuItem, Paper, Tooltip, Typography } from "@mui/material";
import { makeStyles, useTheme } from "@mui/styles";
import { CloudDownload, CopyAll, DriveFileMove, Edit, ExpandMore, FolderCopy, MoreHoriz } from '@mui/icons-material';

import { selectEconomics, updateCurrentEconomics, loadEconomics, updateEconomics, saveEconomics } from '../reducers/economicsSlice';
import { EditEconomics } from './EditEconomics';

import { selectUser } from '../reducers/userSlice';
import { useSnackbar } from 'notistack';
import uuid from 'react-uuid';
import { addProject, selectProjects, updateProject } from '../reducers/projectSlice';
import { EconomicsMoveCopy } from './EconomicsMoveCopy';
import { selectWells } from '../reducers/wellSlice';
import { useSocket } from '../context/useSocket';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: 0,
    marginTop: 10,
    width: '100%'
  },
  well: {
    borderBottom: '1px solid lightgrey',
    margin: 0
  },
  wellOdd: {
    backgroundColor: 'lightgrey'
  },
  wellCurrent: {
    backgroundColor: 'lightgrey',
    borderBottom: '1px solid lightgrey',
    margin: 0
  },
  wellContainer: {
    margin: 0,
    padding: 0,
    borderTop: '1px solid lightgrey'
  },
  listButtons: {
    display: 'flex', 
    justifyContent: 'space-between',
    height: 30,
    margin: '20px 0',
    padding: '0 10px',
    width: '100%'
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
  },
  subheading: {
    fontSize: theme.typography.pxToRem(12),
    marginLeft: 5
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
  },
  wellUwi: {
    fontSize: theme.typography.pxToRem(8),
  },
  icon: {
    verticalAlign: 'bottom',
    height: 20,
    width: 20,
  },
  details: {
    alignItems: 'flex-start',
    padding: 0,
    marginLeft: 0,
    display: 'flex',
    flexDirection: 'column'
  },
  listsButtons: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 10,
    marginBottom: 10
  },
  listToolbar: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: '0 10px',
    margin: 0
  },
  wellButton: {
    margin: '0 10px'
  },
  listInfo: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    width: '100%',
    marginLeft: 6
  },
  List: {
    zIndex: 69,
    backgroundColor: 'white'
  },
  tabs: {
    width: '100%'
  },
  tab: {
    width: 'auto',
    borderBottom: '1px solid lightgrey'
  },
  wellTab: {
    width: '100%',
    height: '100%',
    position: 'relative'
    // backgroundColor: theme.palette.grey[100]
  },
  search: {
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    marginRight: 10,
    marginLeft: 10,
    paddingLeft: 5,
    paddingRight: 5,
    border: `1px solid ${theme.palette.primary.main}`,
    height: 36,
  },
  searchIcon: {
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.primary.main
  },
  inputRoot: {
    color: 'inherit',
    width: '100%'
  },
  inputInput: {
    // vertical padding + font size from searchIcon
    paddingLeft: 24,
  },
}));

export const EconomicsList = ({ economicsId, expandAll }) => {
  
  const theme = useTheme();
  const classes = useStyles();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const { socket } = useSocket();

  const [expanded, setExpanded] = useState(expandAll);
  const [editEconomicsVisible, setEditEconomicsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [economicsMoveCopyVisible, setEconomicsMoveCopyVisible] = useState(false);


  const economics = useSelector(selectEconomics);
  const currentEconomics = economics?.byId?.[economicsId];
  const user = useSelector(selectUser);
  const projects = useSelector(selectProjects);
  const currentProject = projects?.byId?.[projects?.currentProjectId];
  const wells = useSelector(selectWells);

  const darkMode = user?.value?.darkMode;

  useEffect(() => setExpanded(expandAll), [expandAll]);

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setMenuOpen(false);
  };

  return (
    <Paper 
      className={classes.root} 
      onClick={() => makeEconomicsCurrent(economicsId)}
      style={{
        opacity: economicsId === economics?.currentEconomicsId ? 1 : 0.3,
      }}
      elevation={0}
    >
      <Accordion 
        expanded={expanded}
        onClick={() => {}}
        sx={{
          '&:before': {
            display: 'none',
          },
          boxShadow: 1,
          backgroundColor: theme?.palette?.elevationOne?.[darkMode ? 'dark' : 'light']
        }}
        elevation={0}
      >
        <AccordionSummary expandIcon={<ExpandMore />} onClick={() => setExpanded(prev => !prev)}>
          <div style={{textOverflow: 'ellipsis', overflow: 'hidden', maxWidth: 240}}>
            <Typography className={classes.heading} noWrap>{currentEconomics?.name || ""}</Typography>
          </div>
        </AccordionSummary>
        <AccordionDetails className={classes.details}>

        <div className={classes.listInfo}>
          <Typography className={classes.subheading} color="textSecondary">{`Last Modified: ${new Date(currentEconomics?.lastModified)?.toLocaleString()}`}</Typography>
          <Typography className={classes.subheading} color="textSecondary">{`Access: ${currentEconomics?.public ? 'Public' : 'Private'}`}</Typography>
          <Typography className={classes.subheading} color="textSecondary">{`Type: 
            ${currentEconomics?.economicsIds?.length > 0 ? 'Rollup' : currentEconomics?.settings?.consolidation === 'full-cycle' ? 'Full-cycle' : 'Forward-looking'}`}</Typography>
          {currentEconomics?.economicsIds?.length > 0 ? 
            <Typography className={classes.subheading} color="textSecondary">{`Rolled up economics: ${currentEconomics?.economicsIds?.length}`}</Typography>
            :
            <Typography className={classes.subheading} color="textSecondary">{`Wells: ${getNumWells(currentEconomics)} | Type Curves: ${getNumTypeCurves(currentEconomics)} | Group Wells: ${getNumGroupWells(currentEconomics)}`}</Typography>
          }
        </div>


        <div style={{margin: '20px 0 0 10px', display: 'flex', flexDirection: 'column'}}>
          <Typography color="textSecondary" style={{fontSize: 12}}>DESCRIPTION</Typography>
          <Typography color="textPrimary" style={{fontSize: 14, marginTop: 10}}>{currentEconomics?.description || ""}</Typography>
        </div>

        <div className={classes.wellTab}>
          <div className={classes.listButtons}>
            <div style={{display: 'flex'}}>
              <div style={{position: 'relative'}}>
                <Tooltip title="Load economics">
                  <span><Button 
                    size="small" 
                    variant="contained" 
                    color={darkMode ? "inherit" : "primary"} 
                    style={{color: darkMode ? '#000' : '#fff', marginRight: 10}} 
                    startIcon={<CloudDownload />} 
                    onClick={handleLoadEconomics}
                    disabled={isLoading || (currentEconomics.cashflowNet?.length > 0 && currentEconomics.cashflowGross?.length > 0)}
                  >
                    Load
                  </Button></span>
                </Tooltip>
                {isLoading && (
                  <CircularProgress
                    size={24}
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      marginTop: '-11px',
                      marginLeft: '-12px',
                    }}
                  />
                )}
              </div>

              <Button 
                size="small" 
                title="Edit Economics" 
                variant="outlined" 
                color={darkMode ? "inherit" : "primary"} 
                startIcon={<Edit />} 
                onClick={() => setEditEconomicsVisible(true)} 
                disabled={(!currentEconomics?.cashflowNet && !currentEconomics?.cashflowGross) || isLoading}
              >
                Edit
              </Button>

            </div>

            <div>
              <IconButton onClick={handleOpenMenu} style={{height: 30, width: 30}} disabled={(!currentEconomics?.cashflowNet && !currentEconomics?.cashflowGross)}>{<MoreHoriz />}</IconButton>
              <Menu
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleCloseMenu}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "left"
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left"
                }}
              >
                <MenuItem onClick={(e) => handleDuplicateEconomics(e, currentEconomics)}>{<CopyAll style={{marginRight: 10}} />} Duplicate</MenuItem>
                <MenuItem onClick={() => {
                  setEconomicsMoveCopyVisible('copy');
                  handleCloseMenu();
                }}>{<FolderCopy style={{marginRight: 10}} />} Copy</MenuItem>
                <MenuItem onClick={() => {
                  setEconomicsMoveCopyVisible('move');
                  handleCloseMenu();
                }}>{<DriveFileMove style={{marginRight: 10}} />} Move</MenuItem>
              </Menu>
            </div>

            
          </div>
        </div>

        </AccordionDetails>
        <Divider />
      </Accordion>

      {editEconomicsVisible && <EditEconomics handleEditEconomicsClose={handleEditEconomicsClose} economics={currentEconomics} />}
      {economicsMoveCopyVisible && <EconomicsMoveCopy economicsId={currentEconomics?.id} setEconomicsMoveCopyVisible={setEconomicsMoveCopyVisible} handleSaveEconomics={handleSaveEconomics} action={economicsMoveCopyVisible} />}

    </Paper>
  );

  function handleEditEconomicsClose() {
    setEditEconomicsVisible(false);
  };

  function makeEconomicsCurrent(economicsId) {
    dispatch(updateCurrentEconomics({id: economicsId}));
  };

  function handleLoadEconomics() {
    setIsLoading(true);
    dispatch(loadEconomics({location: currentEconomics?.location, economicsId: currentEconomics?.id, email: user?.value?.email})).then((res) => {
      enqueueSnackbar(`Economics ${currentEconomics?.name} loaded successfully`, {variant: 'success'});
      setIsLoading(false);

      // check to make sure that all economics well are processed
      const unprocessedWells = [];

      res?.payload?.economicsWells?.wells?.forEach(well => {
        if (wells?.byUwi?.[well.id]?.status !== 'processed') {
          unprocessedWells.push(well.id);
        };
      });

      if (unprocessedWells?.length > 0) {
        console.log(`Sending process request for ${unprocessedWells?.length} wells...`);
        sendProcessRequest(unprocessedWells, false);
        enqueueSnackbar(`Downloading data for ${unprocessedWells?.length || 0} economics sources, please wait...`, {variant: 'info'});
      };

    }
    ).catch(error => {
      console.log(error);
      setIsLoading(false);
      enqueueSnackbar(`Economics ${currentEconomics?.name} could not be loaded`, {variant: 'error'});
    });
  };

  function sendProcessRequest(uwis, reFit) {
    try {
      let localParams = [];
      currentProject?.wells?.forEach(w => {
        let newWell = {...w};
        let wellParams = {...newWell.params};
        const wellFluids = Object.keys(wellParams)?.filter(f => f !== 'cutoff');
        
        for (let fluid of wellFluids) {
          wellParams[fluid] = {...wellParams[fluid], outliers: []};
        };
        localParams.push({...newWell, params: wellParams});
      });

      if (socket && socket.readyState === 1) {
        socket.send(JSON.stringify({
          messageType: 'process', 
          payload: uwis || [], 
          userEmail: user?.value?.email, 
          params: {},
          localParams,
          reFit,
          assistedForecast: false, 
          tcParams: null
        }));
      };
    } catch (error) {
      console.log(error);
    };
  };

  function handleSaveEconomics() {
    setIsLoading(true);
    dispatch(saveEconomics(currentEconomics)).then(() => {
      enqueueSnackbar(`Economics ${currentEconomics?.name} saved successfully`, {variant: 'success'});

      // save project with new FBA id
      const currentProject = {...projects?.byId?.[projects?.currentProjectId]};

      if (!currentProject.economicsIds || !currentProject.economicsIds.includes(currentEconomics?.id)) {
        if (!currentProject.economicsIds) currentProject.economicsIds = [];
        currentProject.economicsIds = [...currentProject.economicsIds, currentEconomics?.id];
        dispatch(addProject({ project: currentProject, lists: [], email: user?.value?.email }));
      };

      setIsLoading(false);
    }).catch(error => {
      console.log(error);
      enqueueSnackbar(`Economics ${currentEconomics?.name} could not be saved`, {variant: 'error'});
      setIsLoading(false);
    });
  };

  function handleDuplicateEconomics(e, economics) {
    try {
      e.preventDefault();
      e.stopPropagation();
      const id = uuid();
      dispatch(updateEconomics({ 
        economics: {
          ...economics,
          id,
          name: `${economics.name}-copy`,
          createdBy: user?.value?.email,
          public: false,
          location: "",
          projectId: projects?.currentProjectId
        },
        projectId: projects?.currentProjectId
      }));

      // update project with new economics id
      const currentProject = {...projects?.byId?.[projects?.currentProjectId]};

      if (!currentProject.economicsIds || !currentProject.economicsIds.includes(id)) {
        if (!currentProject.economicsIds) currentProject.economicsIds = [];
        currentProject.economicsIds = [...currentProject.economicsIds, id];
        dispatch(updateProject({ project: currentProject, lists: [], email: user?.value?.email }));
      };
      
    } catch (error) {
      console.log(error);
    };
  };


  function getNumWells(economics) {
    try {
      let numWells = 0;

      // economics.economicsWells.lists.forEach(list => {
      //   numWells += lists.byId[list.id]?.wellUwis?.length * list.quantity;
      // });
      economics.economicsWells.wells.forEach(well => {
        numWells += (well?.quantity || 0);
      });
      return numWells;
    } catch (error) {
      return 0;
    };
  };

  function getNumTypeCurves(economics) {
    try {
      let numTypeCurves = 0;
      economics.economicsWells.typeCurves.forEach(typeCurve => {
        numTypeCurves += typeCurve.quantity;
      });
      return numTypeCurves;
    } catch (error) {
      return 0;
    };
  };

  function getNumGroupWells(economics) {
    try {
      let numGroupWells = 0;
      economics.economicsWells.groupWells.forEach(groupWell => {
        numGroupWells += groupWell.quantity;
      });
      return numGroupWells;
    } catch (error) {
      return 0;
    };
  };
};